/**
 * Easter bunting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterBuntingPack: ToilePack = {
    slug: 'easter-bunting',
    category: 'season',
    name: 'Easter bunting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'patterned', label: 'Patterned flags', subject: 'A sheet of triangle bunting flags in a different spring pattern each, ending in one small tag flag lettered exactly "EASTER," dashed cut lines'},
        {key: 'letters', label: 'Letter flags', subject: 'A sheet of triangle bunting flags each holding one letter of "EASTER" in bold, dashed cut lines'},
        {key: 'mini', label: 'Cake flags', subject: 'A sheet of tiny patterned triangle flags for a cake topper string, ending in one small tag flag lettered exactly "EASTER," dashed cut lines'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['spelling a different word instead', 'for a mantel garland, not a party', 'in a longer twelve-flag run'],
    directionHint: 'try: spelling a different word instead',
    seo: {
        title: 'Easter Bunting: Patterned, Letter & Cake Flags',
        metaDescription: "Nine Easter bunting sheets — patterned triangles, letter flags, and mini cake flags — in pastel, vintage, and kids' styles, ready to cut and hang.",
        h1: 'Easter bunting: patterned, letter, and cake flags',
        tagline: 'Nine sheets, three flag sets, three styles each — dashed cut lines around every flag.',
        intro: "Three Easter bunting sheets — patterned triangle flags, letter flags, and tiny cake-topper flags — each in pastel, vintage, and kids' cartoon styles, with dashed cut lines around every piece. Ask for a different word, a longer string, or a matching cake topper, and the same bunting grammar carries it.",
        useCases: [
            'an Easter party garland',
            'a mantel or window display',
            'a cake table decoration',
            'a classroom spring display',
        ],
        faq: [
            {q: 'How do I hang these after printing?', a: 'Cut along the dashed lines and string or tape the flags along ribbon or twine — no punched holes are included in the design.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single landscape page; the flags print at a size ready to cut by hand.'},
            {q: 'Can I get a different word or a longer set?', a: 'Yes — the direction field takes any variation, including a different word or a longer string.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
